import ReservationExternalStep1 from "@/modules/front/ReservationExternal/ReservationExternalStep1";
import React from "react";
import ReservationExternalStep2 from "@/modules/front/ReservationExternal/ReservationExternalStep2";
import FormProgress from "@/modules/front/ReservationExternal/FormProgress";
import moment from "moment/moment";
import OrderService from "@/modules/front/services/OrderService";
import ReservationExternalStepFinish from "@/modules/front/ReservationExternal/ReservationExternalStepFinish";
import ReservationExternalStepBankTransfer from "@/modules/front/ReservationExternal/ReservationExternalStepBankTransfer";
import ReservationExternalStepExtras from "@/modules/front/ReservationExternal/ReservationExternalStepExtras";
import ServiceService from "@/modules/front/services/ServiceService";
import ProductService from "@/modules/front/services/ProductService";

const ReservationExternal = (props) => {
    const [step, setStep] = React.useState(1);
    const [error, setError] = React.useState(null);

    const [dateFrom, setDateFrom] = React.useState(null);
    const [dateTo, setDateTo] = React.useState(null);
    const [totalPrice, setTotalPrice] = React.useState(null);
    const [itemPrice, setItemPrice] = React.useState(null);

    const pricingNote = '';

    const [personalData, setPersonalData] = React.useState({});
    const [selectedServices, setSelectedServices] = React.useState([]);

    const [selectedItems, setSelectedItems] = React.useState([]);

    const [submitting, setSubmitting] = React.useState(false);
    const [hasExtras, setHasExtras] = React.useState(false);
    const [bankTransfer, setBankTransfer] = React.useState(null);

    const handleSelectedServices = (serviceSelections, services, itemIds, items) => {
        // serviceSelections = [{id, quantity}]
        let newSelectedServices = selectedServices.filter((selectedService) => {
            return selectedService.id === undefined;
        });

        const serviceMap = {};
        services.forEach((service) => { serviceMap[service.id] = service; });

        serviceSelections.forEach(({id, quantity}) => {
            const service = serviceMap[id];
            if (service && quantity > 0) {
                newSelectedServices.push({
                    id: service.id,
                    name: service.name,
                    price: service.price,
                    quantity
                });
            }
        });

        setSelectedServices(newSelectedServices);

        // itemIds is [{id, quantity}]
        const itemMap = {};
        items.forEach((item) => { itemMap[item.id] = item; });
        let newSelectedItems = [];
        itemIds.forEach(({id, quantity}) => {
            if (itemMap[id] && quantity > 0) {
                newSelectedItems.push({...itemMap[id], quantity});
            }
        });
        setSelectedItems(newSelectedItems);

        const expandedItemIds = newSelectedItems.flatMap((item) => Array(item.quantity).fill(item.id));

        OrderService.createOrder(
            personalData.firstname,
            personalData.lastname,
            personalData.email,
            personalData.phone,
            dateFrom,
            dateTo,
            [window.variables.entityId, ...expandedItemIds],
            newSelectedServices,
            false,
            personalData.address,
            personalData.city,
            personalData.zip,
            personalData.company_name,
            personalData.company_id,
            personalData.company_vat_id,
            personalData.customValues
        ).then((response) => {
            if (response.error) {
                setError(response.error)
            }
            else {
                setTotalPrice(response.totalPrice);
                setItemPrice(response.itemPrice);
                setError(false);
            }
        });
    }

    const handleDateConfirmed = (dateFrom, dateTo, officeId) => {
        setDateFrom(dateFrom);
        setDateTo(dateTo);
        setError(null);
        
        Promise.all([
            ServiceService.getAll(window.variables.entityId),
            ProductService.getRecommendedItems(window.variables.entityId, dateFrom, dateTo)
        ]).then(([services, recommendedItems]) => {
            const itemsArray = Object.keys(recommendedItems).map((key) => recommendedItems[key]);
            const hasAnyExtras = (services && services.length > 0) || (itemsArray && itemsArray.length > 0);
            setHasExtras(hasAnyExtras);
        }).catch(() => {
            setHasExtras(false);
        });
    }

    const handlePersonalData = (data) => {
        setPersonalData(data);
        OrderService.createOrder(
            data.firstname,
            data.lastname,
            data.email,
            data.phone,
            dateFrom,
            dateTo,
        [window.variables.entityId],
            selectedServices,
            false,
            data.address,
            data.city,
            data.zip,
            personalData.company_name,
            personalData.company_id,
            personalData.company_vat_id,
            data.customValues
            ).then((response) => {
                if (response.error) {
                    setError(response.error)
                }
                else {
                    setStep(hasExtras ? 2 : 3);
                    setTotalPrice(response.totalPrice);
                    setItemPrice(response.itemPrice);
                    setError(null);
                }
            });

    }

    const handleSubmit = () => {
        setSubmitting(true);
        OrderService.createOrder(
            personalData.firstname,
            personalData.lastname,
            personalData.email,
            personalData.phone,
            dateFrom,
            dateTo,
            [window.variables.entityId, ...selectedItems.flatMap((item) => Array(item.quantity || 1).fill(item.id))],
            selectedServices,
            true,
            personalData.address,
            personalData.city,
            personalData.zip,
            personalData.company_name,
            personalData.company_id,
            personalData.company_vat_id,
            personalData.customValues
        ).then((response) => {
            setSubmitting(false);
            if (response.error) {
                setError(response.error)
            }
            else {
                if (response.orderId) {
                    if (response.bankTransfer) {
                        setBankTransfer(response.bankTransfer);
                        setStep(98);
                    } else {
                        setStep(99);
                    }
                }
                else {
                    setError("Něco se pokazilo, zkuste to prosím znovu.")
                }
            }
        });
    }

    const steps = hasExtras 
        ? [
            window.translations.reservationExternal.steps.personalInfo,
            window.translations.reservationExternal.steps.extras,
            window.translations.reservationExternal.steps.summary
          ]
        : [
            window.translations.reservationExternal.steps.personalInfo,
            window.translations.reservationExternal.steps.summary
          ];

    const getCurrentStepForProgress = () => {
        if (step === 98 || step === 99) return null;
        if (hasExtras) {
            return step;
        } else {
            return step === 3 ? 2 : step;
        }
    };

    let stepContent = null;
    switch (step) {
        case 1:
            stepContent = <ReservationExternalStep1 setStep={setStep}
                                                    onPersonalDataSubmitted={handlePersonalData}
                                                    onDateConfirmed={handleDateConfirmed}
                                                    onError={setError}
                                                    dateFrom={dateFrom}
                                                    dateTo={dateTo}
                                                    personalData={personalData}
                                                    pricingNote={pricingNote}
                                                    customFields={props.customFields || []}
                                                    {...props}/>;
            break;
        case 2:
            stepContent = <ReservationExternalStepExtras setStep={setStep}
                                                         setSelectedServices={handleSelectedServices}
                                                         dateFrom={dateFrom}
                                                         dateTo={dateTo}
                                                         itemId={window.variables.entityId}
                                                         pricingNote={pricingNote}
                                                         {...props}/>;
            break;
        case 3:
            stepContent = <ReservationExternalStep2 setStep={setStep}
                                                    personalData={personalData}
                                                    dateFrom={dateFrom}
                                                    dateTo={dateTo}
                                                    totalPrice={totalPrice}
                                                    itemTitle={props.itemTitle}
                                                    itemPrice={itemPrice}
                                                    onSubmit={handleSubmit}
                                                    onDateConfirmed={handleDateConfirmed}
                                                    selectedServices={selectedServices}
                                                    selectedItems={selectedItems}
                                                    onErrors={setError}
                                                    submitting={submitting}
                                                    pricingNote={pricingNote}
                                                    hasExtras={hasExtras}
                                                    {...props}/>;
            break;
        case 98:
            stepContent = <ReservationExternalStepBankTransfer bankTransfer={bankTransfer} />
            break;
        case 99:
            stepContent = <ReservationExternalStepFinish />
            break;
    }

    let errorBox = null;
    if (error) {
        errorBox = <div className="alert alert-danger" role="alert">
            {error}
        </div>;
    }

    const currentStepForProgress = getCurrentStepForProgress();

    return <div>
        {errorBox}
        {currentStepForProgress && <FormProgress steps={steps} currentStep={currentStepForProgress}/>}
        {stepContent}
    </div>

};

export default ReservationExternal;